import { ChevronDown } from "lucide-react";
import { type RefObject, useRef } from "react";
import {
  getPhoneCountry,
  PHONE_COUNTRIES,
  resolvePhoneCountry,
} from "./phone.ts";

export type PhoneInputValue = { value: string; country: string };

export type PhoneInputProps = {
  country: string;
  disabled?: boolean | undefined;
  error?: string | boolean | undefined;
  errorId?: string | undefined;
  id: string;
  inputRef?: RefObject<HTMLInputElement | null> | undefined;
  name?: string | undefined;
  onChange: (value: PhoneInputValue) => void;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  value: string;
};

/** Controlled raw input. The caller validates and normalizes it on submission. */
export function PhoneInput({
  id,
  name,
  value,
  onChange,
  country,
  required,
  disabled,
  inputRef,
  error,
  errorId,
  placeholder = "(201) 555-0123",
}: PhoneInputProps) {
  const input = useRef<HTMLInputElement>(null);
  const selectedCountry = getPhoneCountry(country);

  return (
    <div className="lockerverse-form__phone" dir="ltr">
      <div className="lockerverse-form__phone-country">
        <span aria-hidden="true">
          <span className="lockerverse-form__phone-flag">
            {selectedCountry.label.split(" ")[0]}
          </span>
          {selectedCountry.callingCode}
          <ChevronDown aria-hidden="true" size={14} />
        </span>
        <select
          aria-label="Phone number country"
          disabled={disabled}
          name="phoneCountry"
          onChange={(event) => {
            const nextCountry = getPhoneCountry(event.target.value);
            onChange({ country: nextCountry.value, value });
            input.current?.focus();
          }}
          value={selectedCountry.value}
        >
          {PHONE_COUNTRIES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <input
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
        autoComplete="tel-national"
        disabled={disabled}
        id={id}
        name={name}
        onChange={(event) => {
          const raw = event.target.value;
          onChange({ country: resolvePhoneCountry(raw, country), value: raw });
        }}
        placeholder={placeholder}
        ref={(element) => {
          input.current = element;
          if (inputRef) {
            inputRef.current = element;
          }
        }}
        required={required}
        type="tel"
        value={value}
      />
    </div>
  );
}
